import { Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { PdfViewer } from "@/components/PdfViewer";
import { useIsMobile } from "@/hooks/use-mobile";

interface COIPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pdfData: ArrayBuffer | null;
  pdfBlob: Blob | null;
  downloadFilename: string;
}

export function COIPreviewModal({
  open,
  onOpenChange,
  pdfData,
  pdfBlob,
  downloadFilename,
}: COIPreviewModalProps) {
  const isMobile = useIsMobile();

  const handleDownload = () => {
    if (!pdfBlob) return;
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={handleDownload}
      disabled={!pdfBlob}
      className="gap-1.5"
      data-testid="button-download-coi-preview"
    >
      <Download className="w-4 h-4" />
      Download
    </Button>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[95vh]">
          <DrawerHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pr-12">
            <div className="flex flex-col space-y-1 text-left">
              <DrawerTitle className="text-base font-bold">COI — Certificate Preview</DrawerTitle>
              <DrawerDescription>Preview the generated certificate of insurance</DrawerDescription>
            </div>
            {downloadButton}
          </DrawerHeader>
          <div className="flex-1 min-h-[60vh] overflow-hidden">
            <PdfViewer data={pdfData} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[min(1200px,95vw)] h-[90vh] p-0 gap-0 flex flex-col"
        data-testid="dialog-coi-preview"
      >
        <DialogHeader className="flex flex-row items-start justify-between gap-4 space-y-0 px-6 py-4 border-b pr-16">
          <div className="flex flex-col space-y-1 text-left">
            <DialogTitle className="text-base font-bold">COI — Certificate Preview</DialogTitle>
            <DialogDescription>Preview the generated certificate of insurance</DialogDescription>
          </div>
          {downloadButton}
        </DialogHeader>
        <div className="flex-1 overflow-hidden">
          <PdfViewer data={pdfData} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
